import type { OrderType } from "@lib/types/orderType";
import type { EstimatedTime } from "@lib/zod/deliverySchema";
import { ESTIMATED_TIME_UNITS } from "@lib/zod/deliverySchema";

export const FALLBACK_PICKUP_BUFFER_MINUTES = 20;
export const FALLBACK_DELIVERY_BUFFER_MINUTES = 45;

const UNIT_TO_MINUTES: Record<(typeof ESTIMATED_TIME_UNITS)[number], number> = {
    minutes: 1,
    hours: 60,
    days: 60 * 24,
    weeks: 60 * 24 * 7,
    months: 60 * 24 * 30,
};

export const estimatedTimeText = (
    currentOrderType: OrderType,
    deliveryZoneEstimatedTime?: EstimatedTime,
    defaultEstimatedDeliveryTime?: EstimatedTime,
    defaultEstimatedPickupTime?: EstimatedTime
) => {
    return estimatedTimeText1(
        currentOrderType,
        getEstimatedTimeJSON(
            currentOrderType,
            deliveryZoneEstimatedTime,
            defaultEstimatedDeliveryTime,
            defaultEstimatedPickupTime)
    );
}

export const getEstimatedTimeJSON = (
    currentOrderType: OrderType,
    deliveryZoneEstimatedTime?: EstimatedTime,
    defaultEstimatedDeliveryTime?: EstimatedTime,
    defaultEstimatedPickupTime?: EstimatedTime
) => {
    return currentOrderType === 'delivery'
        ? (deliveryZoneEstimatedTime ||
            defaultEstimatedDeliveryTime)
        : (defaultEstimatedPickupTime);
}

/** Prefer max, else min. Convert unit to minutes. Fallback 20 pickup / 45 delivery. */
export const getEstimatedBufferMinutes = (
    estimatedTime: EstimatedTime | undefined,
    orderType: OrderType,
): number => {
    const fallback = orderType === 'pickup'
        ? FALLBACK_PICKUP_BUFFER_MINUTES
        : FALLBACK_DELIVERY_BUFFER_MINUTES;
    const value = estimatedTime?.max ?? estimatedTime?.min;
    if (value === undefined || value === null || Number.isNaN(value)) {
        return fallback;
    }
    const unit = estimatedTime?.unit ?? 'minutes';
    const multiplier = UNIT_TO_MINUTES[unit] ?? 1;
    return Math.max(0, value * multiplier);
}

export const estimatedTimeText1 = (
    currentOrderType: OrderType,
    estimatedTime: EstimatedTime | undefined,
) => {
    if (!estimatedTime) return undefined;
    const times = [
        estimatedTime.min,
        estimatedTime.max
    ].filter(Boolean);
    let timeLabel = 'Estimation';
    if (currentOrderType === 'delivery') {
        timeLabel = 'Estimated Delivery';
    } else if (currentOrderType === 'pickup') {
        timeLabel = 'Estimated Preparation'
    }
    return times.length ? `${timeLabel}: ${times.join('-')} ${estimatedTime.unit || ''}` : undefined;
}
